import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Table,
  Popconfirm,
  Typography,
  Modal,
  Input,
  Row,
  Col,
  Tooltip,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  QrcodeOutlined,
  ExperimentOutlined,
  SearchOutlined,
  SyncOutlined,
  CopyOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import weworkGroupJoinWayService from '../services/weworkGroupJoinWayService';
import weworkService from '../services/weworkService';
import './WeworkGroupJoinWay.css';

const { Title, Paragraph } = Typography;

const WeworkGroupJoinWay = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groupJoinWays, setGroupJoinWays] = useState([]);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [currentQrCode, setCurrentQrCode] = useState('');
  const [currentGroupJoinWayName, setCurrentGroupJoinWayName] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDownloading, setBatchDownloading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 加载数据
  useEffect(() => {
    loadInitialData();
  }, []);

  // 加载初始数据
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 并行加载群组数据
      const [groupsData] = await Promise.all([
        weworkService.getAllGroups().catch(err => {
          console.error('获取群组数据失败:', err);
          return [];
        })
      ]);

      // 处理群组数据，确保正确格式
      let processedGroups = [];
      if (Array.isArray(groupsData)) {
        processedGroups = groupsData;
      } else if (groupsData && groupsData.content) {
        processedGroups = groupsData.content;
      } else if (groupsData && Array.isArray(groupsData.data)) {
        processedGroups = groupsData.data;
      }

      setGroups(processedGroups || []);

      // 加载群活码数据
      await loadGroupJoinWays();
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载群活码列表
  const loadGroupJoinWays = async (page = 1, pageSize = 10, remarkKeyword = '') => {
    setLoading(true);
    try {
      // 确保参数是有效的数字
      const validPage = Number.isInteger(page) && page > 0 ? page : 1;
      const validPageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;

      const params = {
        page: validPage - 1, // 后端从0开始
        size: validPageSize
      };

      // 添加搜索参数
      if (remarkKeyword && remarkKeyword.trim()) {
        params.remark = remarkKeyword.trim();
      }

      console.log('📝 加载群活码列表参数:', params);

      const data = await weworkGroupJoinWayService.getGroupJoinWays(params);
      if (data.success) {
        setGroupJoinWays(data.data || []);
        setPagination({
          current: validPage,
          pageSize: validPageSize,
          total: data.totalElements || 0
        });
      } else {
        message.error(data.message || '加载群活码列表失败');
      }
    } catch (error) {
      console.error('加载群活码列表失败:', error);
      message.error('加载群活码列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    // 重置到第一页并执行搜索
    const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
    loadGroupJoinWays(1, safePageSize, value);
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchKeyword('');
    // 重置到第一页并清除搜索
    const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
    loadGroupJoinWays(1, safePageSize, '');
  };

  // 处理分页变化
  const handleTableChange = (paginationInfo) => {
    const safeCurrent = Number.isInteger(paginationInfo.current) && paginationInfo.current > 0 ? paginationInfo.current : 1;
    const safePageSize = Number.isInteger(paginationInfo.pageSize) && paginationInfo.pageSize > 0 ? paginationInfo.pageSize : 10;
    loadGroupJoinWays(safeCurrent, safePageSize, searchKeyword);
  };

  // 删除群活码
  const handleDelete = async (id) => {
    try {
      const data = await weworkGroupJoinWayService.deleteGroupJoinWay(id);
      if (data.success) {
        message.success('删除成功');
        // 删除后重新加载，使用安全的分页参数
        const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
        const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
        loadGroupJoinWays(safeCurrent, safePageSize, searchKeyword);
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 复制群活码
  const handleCopyGroupJoinWay = async (record) => {
    try {
      console.log('准备复制群活码:', record);

      // 显示输入框让用户编辑新群活码名称
      Modal.confirm({
        title: '复制群活码',
        content: (
          <div style={{ marginTop: 16 }}>
            <p>将复制群活码「{record.remark || '未命名群活码'}」的所有配置</p>
            <Input
              placeholder="请输入新群活码名称"
              defaultValue={`${record.remark || '未命名群活码'}_复制`}
              id="copy-remark-input"
              maxLength={30}
              showCount
            />
          </div>
        ),
        okText: '确认复制',
        cancelText: '取消',
        width: 480,
        onOk: async () => {
          const remarkInput = document.getElementById('copy-remark-input');
          const newRemark = remarkInput?.value?.trim();

          if (!newRemark) {
            message.error('请输入新群活码名称');
            return Promise.reject();
          }

          try {
            message.loading('正在复制群活码...', 0);

            const copyRequest = {
              remark: newRemark
            };

            const data = await weworkGroupJoinWayService.copyGroupJoinWay(record.id, copyRequest);

            message.destroy(); // 清除loading消息

            if (data.success) {
              message.success(`群活码复制成功：${newRemark}`);
              // 复制成功后重新加载列表
              const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
              const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
              await loadGroupJoinWays(safeCurrent, safePageSize, searchKeyword);
            } else {
              message.error(data.message || '复制失败');
            }
          } catch (error) {
            message.destroy(); // 清除loading消息
            console.error('复制群活码失败:', error);
            const errorMsg = error.response?.data?.message || error.message || '复制失败';
            message.error('复制失败: ' + errorMsg);
            return Promise.reject();
          }
        }
      });
    } catch (error) {
      console.error('复制群活码操作失败:', error);
      message.error('操作失败');
    }
  };

  // 预览二维码
  const handlePreviewQrCode = (record) => {
    if (!record.qrCode) {
      message.warning('该群活码暂无二维码');
      return;
    }
    setCurrentQrCode(record.qrCode);
    setCurrentGroupJoinWayName(record.remark || '群活码');
    setQrCodeModalVisible(true);
  };

  // 下载二维码
  const handleDownloadQrCode = async (record) => {
    if (!record.qrCode) {
      message.warning('该群活码暂无二维码');
      return;
    }

    const fileName = `${record.remark || '群活码'}_二维码.png`;

    try {
      // 尝试使用fetch下载（带上必要的headers）
      const response = await fetch(record.qrCode, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // 检查blob是否有效（大小大于0）
      if (blob.size === 0) {
        throw new Error('获取到的图片数据为空');
      }

      const blobUrl = window.URL.createObjectURL(blob);

      // 创建下载链接
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 清理blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

      message.success('二维码下载成功');
    } catch (error) {
      console.warn('直接下载失败，尝试备用方案:', error.message);

      // 备用方案：使用Canvas转换
      try {
        message.loading('正在准备下载...', 0);
        await downloadViaCanvas(record.qrCode, fileName);
      } catch (fallbackError) {
        message.destroy();
        console.error('所有下载方案都失败:', fallbackError);

        // 最后的备用方案：提示用户手动下载
        Modal.info({
          title: '下载提示',
          content: (
            <div>
              <p>由于网络限制，无法自动下载二维码。</p>
              <p>请点击下方链接，在新页面中右键保存图片：</p>
              <a
                href={record.qrCode}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff' }}
              >
                点击查看二维码
              </a>
            </div>
          ),
          okText: '我知道了'
        });
      }
    }
  };

  // Canvas备用下载方案
  const downloadViaCanvas = (imageUrl, fileName) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;

          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = fileName;
              link.style.display = 'none';

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

              message.destroy();
              message.success('二维码下载成功');
              resolve();
            } else {
              reject(new Error('Canvas转换失败'));
            }
          }, 'image/png');
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      // 设置图片src，触发加载
      img.src = imageUrl;
    });
  };

  // 手动同步群活码
  const handleSyncGroupJoinWays = async () => {
    setSyncLoading(true);
    try {
      message.loading('正在从企业微信同步群活码数据...', 0);

      const data = await weworkGroupJoinWayService.syncGroupJoinWaysFromWework();

      message.destroy(); // 清除loading消息

      if (data.success) {
        message.success(`同步完成，处理了 ${data.syncCount || 0} 个群活码`);
        // 同步完成后重新加载列表，使用安全的分页参数
        const safeCurrent = Number.isInteger(pagination.current) && pagination.current > 0 ? pagination.current : 1;
        const safePageSize = Number.isInteger(pagination.pageSize) && pagination.pageSize > 0 ? pagination.pageSize : 10;
        await loadGroupJoinWays(safeCurrent, safePageSize, searchKeyword);
      } else {
        message.error(data.message || '同步失败');
      }
    } catch (error) {
      message.destroy(); // 清除loading消息
      console.error('同步群活码失败:', error);
      message.error('同步失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncLoading(false);
    }
  };

  // 批量下载二维码
  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要下载的群活码');
      return;
    }

    setBatchDownloading(true);
    try {
      // 获取选中的群活码数据
      const selectedGroupJoinWays = groupJoinWays.filter(item =>
        selectedRowKeys.includes(item.id)
      );

      // 过滤出有二维码的群活码
      const validGroupJoinWays = selectedGroupJoinWays.filter(item => item.qrCode);

      if (validGroupJoinWays.length === 0) {
        message.warning('选中的群活码都没有二维码');
        return;
      }

      if (validGroupJoinWays.length < selectedGroupJoinWays.length) {
        message.info(`${selectedGroupJoinWays.length}个群活码中有${validGroupJoinWays.length}个有二维码，将下载这${validGroupJoinWays.length}个`);
      }

      // 逐个下载二维码
      let successCount = 0;
      for (const groupJoinWay of validGroupJoinWays) {
        try {
          await downloadSingleQrCode(groupJoinWay);
          successCount++;

          // 添加延迟，避免浏览器阻止多个下载
          if (successCount < validGroupJoinWays.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`下载 ${groupJoinWay.remark || '群活码'} 二维码失败:`, error);
        }
      }

      message.success(`成功下载 ${successCount} 个二维码`);

      // 清除选择
      setSelectedRowKeys([]);

    } catch (error) {
      console.error('批量下载失败:', error);
      message.error('批量下载失败');
    } finally {
      setBatchDownloading(false);
    }
  };

  // 单个下载辅助函数
  const downloadSingleQrCode = async (groupJoinWay) => {
    const fileName = `${groupJoinWay.remark || '群活码'}_二维码.png`;

    try {
      // 尝试使用fetch下载（带上必要的headers）
      const response = await fetch(groupJoinWay.qrCode, {
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // 检查blob是否有效
      if (blob.size === 0) {
        throw new Error('获取到的图片数据为空');
      }

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 清理blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

    } catch (error) {
      // 批量下载时使用Canvas备用方案
      try {
        await downloadViaCanvas(groupJoinWay.qrCode, fileName);
      } catch (canvasError) {
        console.error(`下载 ${groupJoinWay.remark || '群活码'} 失败:`, canvasError);
        throw new Error(`${groupJoinWay.remark || '群活码'} 下载失败`);
      }
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '群活码名称',
      dataIndex: 'remark',
      key: 'remark',
      render: (text) => text || '未命名群活码',
      width: 200
    },
    // {
    //   title: '场景',
    //   dataIndex: 'scene',
    //   key: 'scene',
    //   width: 120,
    //   render: (scene) => (
    //     <Tag color={scene === 1 ? 'blue' : 'green'}>
    //       {scene === 1 ? '小程序插件' : '二维码插件'}
    //     </Tag>
    //   )
    // },
    {
      title: '使用群组',
      dataIndex: 'chatIdList',
      key: 'groups',
      width: 200,
      render: (chatIdList, record) => {
        if (!chatIdList) return <span style={{ color: '#999' }}>未配置群组</span>;

        let parsedChatIds = [];

        // 处理不同的数据格式
        if (Array.isArray(chatIdList)) {
          parsedChatIds = chatIdList;
        } else if (typeof chatIdList === 'string') {
          try {
            parsedChatIds = JSON.parse(chatIdList);
          } catch (error) {
            console.warn('解析群组ID列表失败:', chatIdList, error);
            return <span style={{ color: '#f5222d' }}>数据格式错误</span>;
          }
        }

        if (!Array.isArray(parsedChatIds) || parsedChatIds.length === 0) {
          return <span style={{ color: '#999' }}>未配置群组</span>;
        }

        // 根据chatIds查找对应的群组名称
        const displayGroups = parsedChatIds.map((chatId) => {
          const group = groups.find(g => g.chatId === chatId);
          return group ? group.name : `${chatId}(未匹配)`;
        });

        return (
          <div>
            {displayGroups.length <= 3 ? (
              displayGroups.map((groupName, index) => (
                <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>
                  <TeamOutlined style={{ marginRight: 4 }} />
                  {groupName}
                </div>
              ))
            ) : (
              <>
                {displayGroups.slice(0, 2).map((groupName, index) => (
                  <div key={index} style={{ fontSize: '12px', color: '#1890ff' }}>
                    <TeamOutlined style={{ marginRight: 4 }} />
                    {groupName}
                  </div>
                ))}
                <div style={{ fontSize: '12px', color: '#999' }}>
                  +{displayGroups.length - 2}个群组
                </div>
              </>
            )}
          </div>
        );
      }
    },
    {
      title: '自动建群',
      dataIndex: 'autoCreateRoom',
      key: 'autoCreateRoom',
      width: 120,
      render: (autoCreateRoom) => (
        <span style={{
          color: autoCreateRoom ? '#52c41a' : '#faad14',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {autoCreateRoom ? '✓ 是' : '✗ 否'}
        </span>
      )
    },
    {
      title: '群名配置',
      key: 'roomConfig',
      width: 180,
      render: (_, record) => {
        if (!record.autoCreateRoom) {
          return <span style={{ color: '#999' }}>不自动建群</span>;
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {record.roomBaseName && (
              <div style={{ color: '#1890ff' }}>
                前缀：{record.roomBaseName}
              </div>
            )}
            {record.roomBaseId !== null && record.roomBaseId !== undefined && (
              <div style={{ color: '#52c41a' }}>
                起始序号：{record.roomBaseId}
              </div>
            )}
            {!record.roomBaseName && (record.roomBaseId === null || record.roomBaseId === undefined) && (
              <span style={{ color: '#999' }}>未配置</span>
            )}
          </div>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (time) => time ? new Date(time).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handlePreviewQrCode(record)}
            disabled={!record.qrCode}
            className="qr-preview-btn"
            title={record.qrCode ? "预览二维码" : "暂无二维码"}
          >
            预览
          </Button>
          <Button
            type="link"
            onClick={() => handleDownloadQrCode(record)}
            disabled={!record.qrCode}
            className="qr-download-btn"
            title={record.qrCode ? "下载二维码" : "暂无二维码"}
          >
            下载
          </Button>
          {/* <Button
            type="link"
            onClick={() => handleCopyGroupJoinWay(record)}
            className="copy-btn"
            title="复制群活码配置"
          >
            复制
          </Button> */}
          <Button
            type="link"
            onClick={() => navigate(`/wework-group-join-way/edit/${record.id}`)}
            className="edit-btn"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个群活码吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              className="delete-btn"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>社群活码</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                {searchKeyword && (
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    搜索"{searchKeyword}"，共找到 {pagination.total} 条结果
                    <Button
                      type="link"
                      size="small"
                      onClick={handleClearSearch}
                      style={{ padding: '0 4px', fontSize: '12px' }}
                    >
                      清除搜索
                    </Button>
                  </div>
                )}
                <Input.Search
                  placeholder="搜索群活码名称"
                  allowClear
                  enterButton
                  onSearch={handleSearch}
                  style={{ width: 250 }}
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/wework-group-join-way/create')}
                >
                  创建社群活码
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                  loading={batchDownloading}
                  disabled={selectedRowKeys.length === 0}
                >
                  批量下载二维码 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
                </Button>
                {/* <Button
                  icon={<SyncOutlined />}
                  onClick={handleSyncGroupJoinWays}
                  loading={syncLoading}
                  title="从企业微信同步最新群活码数据"
                >
                  同步群活码
                </Button> */}
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          className="group-join-way-table"
          columns={columns}
          dataSource={groupJoinWays}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys, selectedRows) => {
              setSelectedRowKeys(selectedKeys);
            },
            getCheckboxProps: (record) => ({
              // 只有有二维码的群活码才能被选中用于下载
              disabled: !record.qrCode,
              name: record.remark || '群活码',
            }),
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `共 ${total} 条记录，当前显示 ${range[0]}-${range[1]} 条`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
            onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size })
          }}
        />
      </Card>

      {/* 二维码预览Modal */}
      <Modal
        title={`${currentGroupJoinWayName} - 群活码二维码`}
        open={qrCodeModalVisible}
        onCancel={() => setQrCodeModalVisible(false)}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => {
            const record = { qrCode: currentQrCode, remark: currentGroupJoinWayName };
            handleDownloadQrCode(record);
          }}>
            下载二维码
          </Button>,
          <Button key="close" onClick={() => setQrCodeModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={400}
        centered
        className="qr-code-modal"
      >
        <div className="qr-code-preview-container">
          {currentQrCode ? (
            <img
              src={currentQrCode}
              alt="群活码二维码"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : (
            <div className="qr-code-empty-placeholder">
              暂无二维码
            </div>
          )}
          <div className="qr-code-error-placeholder" style={{ display: 'none' }}>
            二维码加载失败
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WeworkGroupJoinWay;